import type { ComponentType } from 'react';

import { action, autorun, computed, makeObservable, observable } from 'mobx';

import {
  SOCKET_USER_STAMP_CARD_REWARD_CLAIMABLE,
  SOCKET_USER_STAMP_CARD_REWARD_CLAIMED,
  SOCKET_USER_STAMP_CARD_STAMP_EARNED,
} from '../../config/constants';
import { getRequest, postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import { devError } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';

import { isStampLockedByTime, type PlayerStampCard } from './types';

const UNLOCK_ANIMATION_MS = 600;

const UNLOCK_CHECK_INTERVAL_MS = 1000;

export class StampCardStore implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    this.setStampCardFlag(resolveFlag('stamp_card_feat_client', false));

    ldClient?.on('ready', () => {
      this.setStampCardFlag(ldClient?.variation('stamp_card_feat_client', false));
    });

    ldClient?.on('change', (changes) => {
      if ('stamp_card_feat_client' in changes) {
        this.setStampCardFlag(changes.stamp_card_feat_client.current);
      }
    });

    const handleSocketEvent = () => {
      this.resetFirstTimeOpen();
      const previousStamps = this.currentStampCard?.stamps ?? [];
      void this.fetch().then(() => {
        // A buy-all completes several stamps in one purchase, so this collects every newly
        // completed stamp rather than just the first one it finds.
        const newlyCompletedIndexes = (this.currentStampCard?.stamps ?? [])
          .filter(
            (stamp) =>
              stamp.status === 'COMPLETED' &&
              !previousStamps.some((prev) => prev.stampIndex === stamp.stampIndex && prev.status === 'COMPLETED'),
          )
          .map((stamp) => stamp.stampIndex)
          .sort((a, b) => a - b);

        const highestNewlyCompleted = newlyCompletedIndexes[newlyCompletedIndexes.length - 1];
        const isNewlyAnnounced =
          highestNewlyCompleted !== undefined && highestNewlyCompleted !== this.lastAnnouncedStampIndex;

        if (isNewlyAnnounced) {
          this.lastAnnouncedStampIndex = highestNewlyCompleted;
          this.setJustStampedIndexes(newlyCompletedIndexes);
        }

        // `shouldStartSequence` matters here: the FeatureManager kicks the sequence itself after its
        // own fetch pass, but a socket-driven refetch lands long after that, so queueing without
        // starting leaves the entry sitting in a queue nobody reads — and the reveal flags this
        // fetch just consumed are one-shot, so the animation is lost for good.
        const shouldStartDialogSequence = this.hasClaimable || this.hasUnseenFreeStampReveal || isNewlyAnnounced;

        if (shouldStartDialogSequence && !this.isStampCardDialogOpen) {
          this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['StampCard'], true);
        }
      });
    };

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;

      if (isUserAuthorized && this.stampCardFlag) {
        socket.on(SOCKET_USER_STAMP_CARD_STAMP_EARNED, handleSocketEvent);
        socket.on(SOCKET_USER_STAMP_CARD_REWARD_CLAIMABLE, handleSocketEvent);
        socket.on(SOCKET_USER_STAMP_CARD_REWARD_CLAIMED, handleSocketEvent);
        socket.on('connect', handleSocketEvent);
        this.unlockCheckIntervalId ??= setInterval(this.checkForUnlocks, UNLOCK_CHECK_INTERVAL_MS);
      } else {
        socket.off(SOCKET_USER_STAMP_CARD_STAMP_EARNED, handleSocketEvent);
        socket.off(SOCKET_USER_STAMP_CARD_REWARD_CLAIMABLE, handleSocketEvent);
        socket.off(SOCKET_USER_STAMP_CARD_REWARD_CLAIMED, handleSocketEvent);
        socket.off('connect', handleSocketEvent);
        if (this.unlockCheckIntervalId) {
          clearInterval(this.unlockCheckIntervalId);
          this.unlockCheckIntervalId = null;
        }
      }
    });
  }

  private unlockCheckIntervalId: ReturnType<typeof setInterval> | null = null;

  private unlockAnimationTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Server marks-and-reveals a stamp's unlock flourish on the GET response itself (no client POST
  // — see `resolveFirstReveal` in core's player-stamp-card.service.ts): the first fetch to observe
  // a stamp past its `unlocksAt` gets `unlockAnimationShown: true` exactly once, ever, from any
  // platform. This set just makes sure THIS session only asks the server once per stamp — without
  // it, the 1s clock check below would keep re-fetching every tick for a stamp it already asked
  // about (whether the answer was "yes, reveal it" or "someone else already did").
  private checkedUnlockKeys = new Set<string>();

  private checkForUnlocks = (): void => {
    const card = this.currentStampCard;
    if (!card) {
      return;
    }

    const newlyUnlockedStamp = card.stamps.find((stamp) => {
      const key = `${card.userStampCardId}:${stamp.stampIndex}`;
      return stamp.unlocksAt && !isStampLockedByTime(stamp) && !this.checkedUnlockKeys.has(key);
    });

    if (newlyUnlockedStamp) {
      this.checkedUnlockKeys.add(`${card.userStampCardId}:${newlyUnlockedStamp.stampIndex}`);
      void this.confirmUnlockReveal(newlyUnlockedStamp.stampIndex);
    }
  };

  private async confirmUnlockReveal(stampIndex: number): Promise<void> {
    await this.fetch();

    const stamp = this.currentStampCard?.stamps.find((s) => s.stampIndex === stampIndex);
    if (!stamp?.unlockAnimationShown) return;

    if (this.unlockAnimationTimeoutId) {
      clearTimeout(this.unlockAnimationTimeoutId);
    }
    this.setJustUnlockedIndex(stampIndex);
    this.unlockAnimationTimeoutId = setTimeout(() => this.setJustUnlockedIndex(null), UNLOCK_ANIMATION_MS);
  }

  public dialogTypes: FeatureManagerDialogType[] = ['StampCard'];

  public StampCardDialogComponent: ComponentType | null = null;

  private isFetching: boolean = false;

  private isFirstTimeOpen: boolean = false;

  private lastAnnouncedStampIndex: number | null = null;

  @observable
  public stampCardFlag: boolean = false;

  @observable
  public loading: boolean = false;

  @observable
  public currentStampCard: PlayerStampCard | null = null;

  @observable
  public isStampCardDialogOpen: boolean = false;

  @observable
  public justStampedIndexes: number[] = [];

  @observable
  public justUnlockedIndex: number | null = null;

  @computed
  public get claimableStamp() {
    return (
      this.currentStampCard?.stamps.find((stamp) => stamp.hasReward && !stamp.rewardClaimed && stamp.completedAt) ??
      null
    );
  }

  @computed
  public get hasClaimable() {
    return !!this.claimableStamp;
  }

  // True on the exact fetch that reveals a free (auto-granted) stamp's grant animation for the
  // first time ever — same one-shot server field as the unlock flourish, so this flips back to
  // false again once the dialog has had a chance to play it (see StampCardDialog's hydration
  // effect, which force-fires the `stamped` trigger for any stamp this is true for).
  @computed
  public get hasUnseenFreeStampReveal() {
    return !!this.currentStampCard?.stamps.some((stamp) => stamp.isFree && stamp.freeStampAnimationShown);
  }

  @computed
  public get showInStore() {
    return !!this.currentStampCard?.storeVisible;
  }

  @action
  public setFetching(fetching: boolean) {
    this.isFetching = fetching;
  }

  @action
  private setStampCardFlag(flag: boolean) {
    this.stampCardFlag = flag;
  }

  @action
  public setLoading(loading: boolean) {
    this.loading = loading;
  }

  @action
  private setStampCardDialogOpen(isOpen: boolean) {
    this.isStampCardDialogOpen = isOpen;
  }

  @action
  public setCurrentStampCard(card: PlayerStampCard | null) {
    this.currentStampCard = card;
  }

  @action
  public setJustStampedIndexes(stampIndexes: number[]) {
    this.justStampedIndexes = stampIndexes;
  }

  @action
  public setJustUnlockedIndex(stampIndex: number | null) {
    this.justUnlockedIndex = stampIndex;
  }

  // The server hands out `freeStampAnimationShown: true` on exactly one GET, but the card it came
  // on then sits in this store until the next fetch — so the dialog would replay the grant
  // animation on every reopen, and `hasUnseenFreeStampReveal` would keep re-queueing the pop-up.
  // Once the dialog has actually played it, the reveal is spent locally too.
  @action
  public consumeFreeStampReveals() {
    if (!this.currentStampCard) return;

    this.currentStampCard = {
      ...this.currentStampCard,
      stamps: this.currentStampCard.stamps.map((stamp) =>
        stamp.freeStampAnimationShown ? { ...stamp, freeStampAnimationShown: false } : stamp,
      ),
    };
  }

  private resetFirstTimeOpen() {
    this.isFirstTimeOpen = false;
  }

  @action
  public reset() {
    this.currentStampCard = null;
    this.loading = false;
    this.isFetching = false;
    this.isFirstTimeOpen = false;
    this.lastAnnouncedStampIndex = null;
    this.justStampedIndexes = [];
    this.justUnlockedIndex = null;
    this.checkedUnlockKeys.clear();
    if (this.unlockAnimationTimeoutId) {
      clearTimeout(this.unlockAnimationTimeoutId);
      this.unlockAnimationTimeoutId = null;
    }
    if (this.unlockCheckIntervalId) {
      clearInterval(this.unlockCheckIntervalId);
      this.unlockCheckIntervalId = null;
    }
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    if (!this.stampCardFlag) return 'OFF';

    try {
      this.setFetching(true);

      const res = await getRequest<{ data: { currentStampCard?: PlayerStampCard | null } }>('stamp-card/player');
      const { currentStampCard } = res.data;

      this.setCurrentStampCard(currentStampCard ?? null);

      if ((this.hasClaimable || this.hasUnseenFreeStampReveal) && !this.isStampCardDialogOpen) {
        this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['StampCard']);
      }

      return 'READY';
    } catch (error) {
      devError('Error fetching stamp card', error);
      return 'ERROR';
    } finally {
      this.setFetching(false);
    }
  });

  public startDialogs(_dialogType?: FeatureManagerDialogType) {
    if (this.currentStampCard && (this.hasClaimable || this.hasUnseenFreeStampReveal) && !this.isFirstTimeOpen) {
      this.isFirstTimeOpen = true;
      this.openStampCardDialog();
      return true;
    }

    return false;
  }

  public openStampCardDialog() {
    if (!this.StampCardDialogComponent) return;

    this.setStampCardDialogOpen(true);
    this.rootStore.portalStore.openPortal(this.StampCardDialogComponent, {
      beforeClose: () => {
        this.setStampCardDialogOpen(false);
        this.rootStore.featureManagerStore.onDialogFinished('StampCard');
      },
    });
  }

  public async claimReward(userStampCardId: string) {
    this.setLoading(true);

    try {
      await postRequest('stamp-card/player/stamp/claim', { userStampCardId });
      await this.fetch();
    } catch (error) {
      devError('Error claiming stamp card reward', error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }
}
